<template>
  <m-card
    :class="{ 'cursor-pointer': readOnly }"
    sr-label="View procedure"
    @click="procedureOverview()"
  >
    <div
      :class="[
        {
          clickable: !readOnly,
          'marked-incorrect': entry.data.isMarkedIncorrect,
        },
      ]"
    >
      <m-layout-stack horizontal>
        <div class="flex-auto">
          <span><b>Procedure:</b> {{ getProcedureAndBody }}</span>
          <span> • {{ entry.data.performingOrganisation }}</span>
        </div>
        <i v-if="entry.data.isRetrospectivelyAmended" style="white-space: nowrap">{{
          formatRetrospectivelyAmendedLabel
        }}</i>
      </m-layout-stack>
      <div
        v-if="
          entry.data.hiddenFromPatientFacingServices ||
          entry.data.confidentialFromThirdParties ||
          entry.data.linkedProblems.length ||
          entry.data.isExplicitlyIncludedInSCR
        "
      >
        <span class="secondary-text">
          {{ getConfidentialityTextAndLinkedProblems() }}
        </span>
      </div>
      <m-separator v-if="entry.data.additionalInformation" class="q-my-xs" />
      <span v-if="entry.data.additionalInformation">
        {{ entry.data.additionalInformation }}
      </span>
    </div>
  </m-card>
</template>

<script setup lang="ts">
import { openSlideover } from "../../../../composables/dialog/drawer";
import { PatientJournalRecord } from "../types";
import { computed } from "vue";
import MSeparator from "../../MSeparator";

const props = defineProps<{
  entry: PatientJournalRecord;
  readOnly: boolean;
  clickable?: boolean;
}>();

function procedureOverview() {
  props.readOnly
    ? openSlideover("/clinical/procedure/overview/" + props.entry.id, { close: true })
    : undefined;
}

const formatRetrospectivelyAmendedLabel = computed(() => {
  let label = [];
  if (props.entry.data.isRetrospectivelyAmended) {
    label.push("Edited");
  }

  return label.join(" • ");
});

const getProcedureAndBody = computed(() => {
  const textAsArray = [props.entry.data.procedureCode, props.entry.data.body];
  const text = textAsArray.filter((x) => x).join(" • ");
  return text === "" ? false : text;
});

function getConfidentialityTextAndLinkedProblems() {
  const textArray = [];

  if (props.entry.data.hiddenFromPatientFacingServices) {
    textArray.push("Redacted from Patient Online Access");
  }
  if (props.entry.data.confidentialFromThirdParties) {
    textArray.push("Confidential from Third Parties");
  }
  if (props.entry.data.isExplicitlyIncludedInSCR) {
    textArray.push("Explicitly included in SCR");
  }

  const text = textArray.join(" • ");

  const problemsArray = props.entry.data.linkedProblems || [];
  const problemDescriptions = problemsArray
    .filter((x) => x.problemCodeDescription)
    .map((x) => x.problemCodeDescription);

  const problemText = problemDescriptions.length
    ? `Linked Problems: ${problemDescriptions.join(", ")}`
    : "";

  const finalTextArray = [text, problemText].filter(Boolean);

  return finalTextArray.length ? finalTextArray.join(" • ") : false;
}
</script>
